"use client";

import React, { useState, useTransition } from "react";
import { verifyEmailCode } from "../../../app/actions/auth-custom";
import ErrorAlert from "./ErrorAlert";
import { SSO_REQUIRED_ERROR } from "../../lib/auth-constants";
import { captureProductClientEvent } from "@/lib/analytics/product-client-event";
import { ANALYTICS_EVENTS } from "@/lib/analytics/events";
import { normalizeAuthError } from "@/lib/analytics/auth-errors";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Note } from "@/components/ui/note";

type PendingState =
  | { type: "email-verification"; token: string; email: string }
  | { type: "mfa-challenge"; token: string; challengeId: string }
  | { type: "mfa-enrollment"; token: string; challengeId: string; totpUri: string; totpSecret: string };

interface EmailVerificationFormProps {
  pendingToken: string;
  email: string;
  /**
   * Which flow mounted this step. Required (not defaulted) because it changes
   * analytics semantics: a completed signup email-verification must emit
   * AUTH_SIGNUP, a login email-verification must not. Defaulting it would
   * silently mis-count the signup success rate. See BRE-4754.
   */
  flow: "signup" | "login";
  onSuccess: () => void;
  /** BRE-8618: where an org's own sign-in returns to, if this step is sent there. */
  returnTo: string | undefined;
  /** BRE-8955: the org requires SSO; the parent goes back to where the SSO button is. */
  onSsoRequired?: () => void;
  onPendingState?: (state: PendingState) => void;
}

export default function EmailVerificationForm({ pendingToken, email, flow, onSuccess, onPendingState, returnTo, onSsoRequired }: EmailVerificationFormProps) {
  const [isPending, startTransition] = useTransition();
  const [code, setCode] = useState("");
  const [error, setError] = useState("");

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError("");
    startTransition(async () => {
      const result = await verifyEmailCode(code, pendingToken, returnTo);
      if (result.success) {
        captureProductClientEvent(ANALYTICS_EVENTS.AUTH_EMAIL_VERIFIED);
        // BRE-4754: a signup that requires email verification completes HERE,
        // not on SignupForm's immediate success — so AUTH_SIGNUP was
        // never firing for this (common) path, understating the signup success
        // rate. Fire it exactly once, only for the signup flow. A login
        // email-verification must not emit AUTH_SIGNUP.
        if (flow === "signup") {
          captureProductClientEvent(ANALYTICS_EVENTS.AUTH_SIGNUP, { method: "email_verification" });
        }
        onSuccess();
      } else if ("signInUrl" in result && result.signInUrl) {
        // BRE-8618: the org this sign-in lands in requires its own sign-in (SSO).
        window.location.assign(result.signInUrl);
      } else if ("pendingAction" in result) {
        // Email verification may trigger MFA as the next step
        if (result.pendingAction === "mfa-challenge" && onPendingState) {
          captureProductClientEvent(ANALYTICS_EVENTS.AUTH_MFA_CHALLENGED, { trigger: "post_email_verify" });
          onPendingState({ type: "mfa-challenge", token: result.pendingToken, challengeId: result.challengeId });
        } else if (result.pendingAction === "mfa-enrollment" && onPendingState) {
          captureProductClientEvent(ANALYTICS_EVENTS.AUTH_MFA_CHALLENGED, { trigger: "post_email_verify", mfa_type: "enrollment" });
          onPendingState({
            type: "mfa-enrollment",
            token: result.pendingToken,
            challengeId: result.challengeId,
            totpUri: result.totpUri,
            totpSecret: result.totpSecret,
          });
        }
      } else if ("error" in result) {
        captureProductClientEvent(ANALYTICS_EVENTS.AUTH_EMAIL_VERIFY_FAILED, { error_code: normalizeAuthError(result.error) });
        if (result.error === SSO_REQUIRED_ERROR && onSsoRequired) {
          onSsoRequired();
          return;
        }
        setError(result.error);
      }
    });
  }

  return (
    <div className="space-y-4">
      <Note size="sm">
        We sent a verification code to <span className="font-medium">{email}</span>
      </Note>

      {error && <ErrorAlert message={error} />}

      <form onSubmit={handleSubmit} className="space-y-3">
        <label htmlFor="email-verify-code" className="sr-only">Verification code</label>
        <Input
          textShortcuts={false}
          id="email-verify-code"
          type="text"
          value={code}
          onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
          placeholder="Enter 6-digit code"
          required
          inputMode="numeric"
          pattern="^\d{6}$"
          autoComplete="one-time-code"
          autoFocus
          testId="email-verify-code-input"
          variant="onboarding"
          fontSize="sm"
          inputClassName="px-0 text-center tracking-[0.5em] font-mono"
        />
        <Button
          type="submit"
          disabled={isPending || code.length !== 6}
          data-testid="email-verify-submit-button"
          className="w-full"
        >
          {isPending ? "Verifying..." : "Verify email"}
        </Button>
      </form>
    </div>
  );
}
